import { Button } from "@/components/ui/button";
import { Grid } from "@/components/ui/grid";
import { Section, SectionHeader } from "@/components/ui/section";
import { useAdminMode } from "@/contexts/admin-mode-provider";
import { useTeams } from "@/contexts/teams-provider";
import { useTranslation } from "@/hooks/useTranslation";
import { getPromptId } from "@/lib/prompt-utils";
import { PromptCategoryId, PromptDTO } from "@shaarp/common";
import { ChevronDown, ChevronUp } from "lucide-react";
import { memo, useMemo, useState } from "react";
import PromptCard from "./prompt-card";

type PromptsSectionProps = {
  prompts: PromptDTO[];
  onSelectPrompt: (prompt: PromptDTO) => void;
  onEditPrompt?: (prompt: PromptDTO) => void;
  onRemovePrompt?: (prompt: PromptDTO) => void;
  initialVisibleCount?: number;
  title?: string;
  description?: string;
  sectionId?: string;
  favoritePromptIds?: Set<string>;
  onToggleFavorite?: (prompt: PromptDTO) => void;
  pinnedPromptIds?: Set<string>;
  onTogglePinned?: (prompt: PromptDTO) => void;
  categoryId?: PromptCategoryId;
};

function PromptsSection({
  prompts,
  onSelectPrompt,
  onEditPrompt,
  onRemovePrompt,
  initialVisibleCount = 4,
  title,
  description,
  sectionId,
  favoritePromptIds = new Set(),
  onToggleFavorite,
  pinnedPromptIds = new Set(),
  onTogglePinned,
  categoryId,
}: PromptsSectionProps) {
  const { t } = useTranslation();
  const { isAdmin, isManager } = useTeams();
  const { isAdminMode } = useAdminMode();
  const [showAll, setShowAll] = useState(false);

  const processedPrompts = useMemo(() => {
    const sorted = [...prompts].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    return showAll ? sorted : sorted.slice(0, initialVisibleCount);
  }, [prompts, showAll, initialVisibleCount]);

  const hasMore = prompts.length > initialVisibleCount;

  const canEditPrompt = (prompt: PromptDTO) => {
    if (prompt.ownerType === "user") return true;
    if (prompt.ownerType === "team" && (isManager || isAdmin)) return true;
    if (prompt.ownerType === "public" && isAdmin && isAdminMode) return true;
    return false;
  };

  if (prompts.length === 0) {
    return null;
  }

  const finalSectionId =
    sectionId || (categoryId ? `category-${categoryId}` : "prompts");

  return (
    <Section id={finalSectionId}>
      <SectionHeader title={title || ""} description={description} />

      <Grid cols={2} gap="md">
        {processedPrompts.map((prompt) => {
          const canEdit = canEditPrompt(prompt);
          const promptId = getPromptId(prompt);
          const isFavorite = favoritePromptIds.has(promptId);
          const isPinned = pinnedPromptIds.has(promptId);

          return (
            <PromptCard
              key={promptId}
              prompt={prompt}
              onSelect={onSelectPrompt}
              onEdit={canEdit ? onEditPrompt : undefined}
              onRemove={canEdit ? onRemovePrompt : undefined}
              canEdit={canEdit}
              isFavorite={isFavorite}
              onToggleFavorite={onToggleFavorite}
              isPinned={isPinned}
              onTogglePinned={onTogglePinned}
            />
          );
        })}
      </Grid>

      {hasMore && (
        <div className="flex justify-center mt-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAll(!showAll)}
            className="px-6 text-sm border rounded-full text-muted-foreground hover:text-foreground border-border/50 hover:border-border"
          >
            {showAll ? (
              <>
                <ChevronUp className="w-4 h-4 mr-2" />
                {t("prompt.library.showLess")}
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4 mr-2" />
                {t("prompt.library.showMore", {
                  count: prompts.length - initialVisibleCount,
                })}
              </>
            )}
          </Button>
        </div>
      )}
    </Section>
  );
}

export default memo(PromptsSection);
